import { toFirstLowerCase, toFirstUpperCase } from 'infrastructure/functions';

import { deleteUnnecessaryEmptyLines } from '../helpers';

type TData = {
  usedDevice: [];
  lastUseDuration: null | string;
  reasonForStop: [];
  realTimeReading: null | string;
  isNormalLimits: null | string;
  repeatReading: null | string;
  importanceBloodPressure: [];
  raiseBloodPressure: [];
  lowerBloodPressure: [];
  importanceWeight: [];
  importanceBloodSugar: [];
  raiseBloodSugar: [];
  lowerBloodSugar: [];
  importanceOxygen: [];
  additionalDetailsMain: null | string;
  whichPerson: string;
  otherPerson: string;
};

const joinArray = (arr: string[]) => arr?.join(', ');

export const generateNoReadingsMessage = <T extends TData>(data: T) => {
  const {
    usedDevice,
    lastUseDuration,
    reasonForStop,
    realTimeReading,
    isNormalLimits,
    repeatReading,
    importanceBloodPressure,
    raiseBloodPressure,
    lowerBloodPressure,
    importanceWeight,
    importanceBloodSugar,
    raiseBloodSugar,
    lowerBloodSugar,
    importanceOxygen,
    additionalDetailsMain = '',
    whichPerson,
    otherPerson,
  } = data;
  let message = '';
  const isPatient = whichPerson === 'Patient';
  const target =
    whichPerson === 'Other'
      ? toFirstLowerCase(otherPerson as string)
      : toFirstLowerCase(whichPerson as string);

  const speakWith = isPatient ? 'patient' : `${target}`;

  const speakWithUpperCase = isPatient
    ? 'Patient'
    : `${toFirstUpperCase(target)}`;

  if (usedDevice.length > 0 && lastUseDuration) {
    message += `Patient has not used their ${joinArray(
      usedDevice,
    )} in ${lastUseDuration} Days. `;
  }
  if (reasonForStop.length > 0) {
    if (isPatient) {
      message += `${speakWithUpperCase} states they stopped using their device due to: ${joinArray(
        reasonForStop,
      )}. `;
    }
    if (!isPatient) {
      message += `${speakWithUpperCase} states that the patient stopped using the device due to: ${joinArray(
        reasonForStop,
      )}. `;
    }
  }
  if (realTimeReading === 'yes') {
    message += '\n';
    message += '\n';
    message += `Patient is able to take another reading at the time of call. `;
  }
  if (realTimeReading === 'yes' && isNormalLimits === `yes`) {
    message += '\n';
    message += '\n';
    message += `The repeat reading was within normal limits. `;
    if (repeatReading) {
      message += `The repeat reading was ${repeatReading} `;
    }
  }
  if (realTimeReading === 'yes' && isNormalLimits === `no`) {
    message += '\n';
    message += '\n';
    message += `The repeat reading was not within normal limits. Call escalated to nursing team. `;
    if (repeatReading) {
      message += `The repeat reading was ${repeatReading} `;
    }
  }
  if (realTimeReading === 'no') {
    message += '\n';
    message += '\n';
    message += `Patient is not able to take a reading at this time. `;
  }
  if (importanceBloodPressure.length > 0) {
    message += '\n';
    message += '\n';
    message += `MA provided ${speakWith} education on the importance of monitoring ${
      isPatient ? 'their' : `patient's`
    } blood pressure and pulse. ${joinArray(importanceBloodPressure)}. `;
  }

  if (raiseBloodPressure.length > 0) {
    message += '\n';
    message += '\n';
    message += `MA educated ${speakWith} on the following situations and how ${
      isPatient ? 'they' : speakWith
    } can raise ${
      isPatient ? 'their' : "patient's"
    } blood pressure: ${joinArray(raiseBloodPressure)}. `;
  }

  if (lowerBloodPressure.length > 0) {
    message += '\n';
    message += '\n';
    message += `MA educated ${speakWith} on the following situations and how ${
      isPatient ? 'they' : speakWith
    } can drop ${isPatient ? 'their' : "patient's"} blood pressure: ${joinArray(
      lowerBloodPressure,
    )}. `;
  }

  if (importanceWeight.length > 0) {
    message += '\n';
    message += '\n';
    message += `MA provided ${speakWith} education on the importance of monitoring ${
      isPatient ? 'their' : `patient's`
    } weight when ${
      isPatient ? 'they have' : `patient has`
    } heart failure. ${joinArray(importanceWeight)}. `;
  }
  if (importanceBloodSugar.length > 0) {
    message += '\n';
    message += '\n';
    message += `MA provided ${speakWith} education on the importance of monitoring ${
      isPatient ? 'their' : `patient's`
    } blood sugar. ${joinArray(importanceBloodSugar)}. `;
  }
  if (raiseBloodSugar.length > 0) {
    message += '\n';
    message += '\n';
    message += `MA educated ${speakWith} on the following situations and how ${
      isPatient ? 'they' : speakWith
    } can raise ${isPatient ? 'their' : `patient's`} blood sugar: ${joinArray(
      raiseBloodSugar,
    )}. `;
  }
  if (lowerBloodSugar.length > 0) {
    message += '\n';
    message += '\n';
    message += `MA educated ${speakWith} on the following situations and how ${
      isPatient ? 'they' : speakWith
    } can drop ${isPatient ? 'their' : `patient's`} blood sugar: ${joinArray(
      lowerBloodSugar,
    )}. `;
  }
  if (importanceOxygen.length > 0) {
    message += '\n';
    message += '\n';
    message += `MA provided ${speakWith} education on the importance of monitoring ${
      isPatient ? 'their' : `patient's`
    } pulse oximetry. ${joinArray(importanceOxygen)}. `;
  }
  if (additionalDetailsMain) {
    message += '\n';
    message += '\n';
    message += `${additionalDetailsMain} `;
  }

  message = deleteUnnecessaryEmptyLines(message);
  return message;
};
